import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { I18nPipe, ALAIN_I18N_TOKEN } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NZ_MODAL_DATA, NzModalModule, NzModalRef } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { finalize } from 'rxjs';
import { I18NService } from 'src/app/core/i18n/i18n.service';
import { HttpService } from 'src/app/http/http.service';
import { AvatarUploadModalComponent } from 'src/app/routes/avatar-modal/avatar-upload-modal.component';
import { ImageService } from 'src/app/services/image.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

import { AiffSettingListComponent } from './setting/list/aiff-setting-list.component';

@Component({
  selector: 'aiff-detail',
  templateUrl: './aiff-detail.component.html',
  styleUrls: ['./aiff-detail.component.less'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    NzFormModule,
    NzButtonModule,
    NzCardModule,
    NzInputModule,
    NzSelectModule,
    NzIconModule,
    NzSpinModule,
    NzModalModule,
    NzDividerModule,
    NzGridModule,
    NzSwitchModule,
    AiffSettingListComponent,
    NzTabsModule,
    AvatarUploadModalComponent,
    I18nPipe,
    TestIdDirective
  ]
})
export class AiffDetailComponent implements OnInit {
  // 表单
  aiffForm!: FormGroup;
  // 加载状态
  loading = false;
  // 提交状态
  submitting = false;
  // 是否为新增
  isAdd = true;
  isEdit = false;
  // AIFF ID
  aiffId: string | null = null;
  latestRefreshTime?: number;
  iconUrl = '';
  isIconModalVisible = false;
  tempIconFile: File | null = null;

  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);

  constructor(
    private readonly fb: FormBuilder,
    private readonly httpService: HttpService,
    private readonly message: NzMessageService,
    private readonly cdr: ChangeDetectorRef,
    private readonly imageService: ImageService
  ) {
    this.aiffForm = this.fb.group({
      name: [null, [Validators.required]],
      description: [null, [Validators.required]],
      endPointUrl: [null, [Validators.required, Validators.pattern(/^(http|https):\/\/[^ "']+$/)]],
      extraId: [null],
      pictureId: [null],
      pictureUrl: [null]
    });
  }

  private readonly modalRef = inject(NzModalRef, { optional: true });
  private readonly nzModalData = inject(NZ_MODAL_DATA, { optional: true }) || {};

  // 添加 tenantId 属性
  tenantId: string = '';

  // 在 ngOnInit 中获取 tenantId
  ngOnInit(): void {
    // 从模态窗口数据中获取参数
    if (this.nzModalData) {
      this.aiffId = this.nzModalData.aiffId || null;
      this.isAdd = this.nzModalData.isAdd !== undefined ? this.nzModalData.isAdd : true;
      this.tenantId = this.nzModalData.tenantId || null; // 获取 tenantId
    }

    if (!this.isAdd && this.aiffId) {
      this.loadAiffData(this.aiffId);
    }
  }

  // 加载AIFF数据
  loadAiffData(id: string): void {
    this.loading = true;
    this.httpService
      .post(`${ConstantUtil.AIFF_ITEM}/${id}`, {})
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res.success) {
          this.aiffForm.patchValue(res.data || {});
          this.tenantId = res.data.tenantId;
          if (res.data.pictureId) {
            this.loadIconImage(res.data.pictureId);
          } else {
            this.iconUrl = res.data.pictureUrl || '';
          }
          this.cdr.detectChanges();
        } else {
          this.message.error(this.i18n.fanyi('tenant.aiff.detail.load.failed'));
        }
      });
  }

  // 保存AIFF
  saveAiff(): void {
    if (this.aiffForm.invalid) {
      // 触发所有表单验证
      Object.values(this.aiffForm.controls).forEach(control => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
      return;
    }

    const hasPicture = Boolean(
      this.tempIconFile || this.iconUrl || this.aiffForm.get('pictureId')?.value || this.aiffForm.get('pictureUrl')?.value
    );
    if (!hasPicture) {
      this.message.warning(this.i18n.fanyi('tenant.aiff.picture.required'));
      return;
    }

    this.submitting = true;
    const formData = this.aiffForm.value;
    const requestParams = {
      ...(this.isAdd || !this.aiffId ? {} : { id: this.aiffId }),
      extraId: formData.extraId,
      tenantId: this.tenantId,
      description: formData.description,
      name: formData.name,
      ...(this.isAdd ? { pictureUrl: formData.pictureUrl, pictureId: formData.pictureId } : {}),
      endPointUrl: formData.endPointUrl
    };

    const creating = this.isAdd;
    const apiUrl = this.isAdd ? ConstantUtil.AIFF_CREATE : ConstantUtil.AIFF_UPDATE;
    const request$ = this.httpService.postFormData(apiUrl, requestParams, this.tempIconFile, 'icon');
    request$
      .pipe(
        finalize(() => {
          this.submitting = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        const saveSucceeded = res.success && (creating || res.data === true);
        if (saveSucceeded) {
          this.isEdit = true;
          if (creating && res.data && typeof res.data === 'object') {
            this.aiffForm.patchValue(res.data);
            this.latestRefreshTime = this.parseRefreshTime(res.data.refreshTime);
          }
          this.message.success(creating ? this.i18n.fanyi('tenant.aiff.create.success') : this.i18n.fanyi('tenant.aiff.update.success'));
          if (creating && res.data?.id) {
            this.aiffId = res.data.id;
            this.isAdd = false;
          }
          this.cdr.detectChanges();
        } else {
          this.message.error(
            res.msg || (creating ? this.i18n.fanyi('tenant.aiff.create.failed') : this.i18n.fanyi('tenant.aiff.update.failed'))
          );
        }
      });
  }

  showIconUploadModal(): void {
    this.isIconModalVisible = true;
  }

  handleIconModalCancel(): void {
    this.isIconModalVisible = false;
  }

  handleIconCropped(event: { blob: Blob; width: number }): void {
    this.tempIconFile = new File([event.blob], 'aiff-icon.png', { type: event.blob.type || 'image/png' });
    const reader = new FileReader();
    reader.onload = () => {
      this.iconUrl = typeof reader.result === 'string' ? reader.result : '';
      this.handleIconModalCancel();
      this.cdr.detectChanges();
    };
    reader.readAsDataURL(this.tempIconFile);
  }

  loadIconImage(pictureId: string): void {
    this.imageService.loadImage(pictureId, 'M').subscribe(imageUrl => {
      this.iconUrl = imageUrl || this.aiffForm.get('pictureUrl')?.value || '';
      this.cdr.detectChanges();
    });
  }

  private parseRefreshTime(value: unknown): number | undefined {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  }

  // 取消
  cancel(): void {
    if (this.submitting) {
      return;
    }

    if (this.modalRef) {
      this.modalRef.close(
        this.isEdit
          ? {
              edited: true,
              refreshTime: this.latestRefreshTime
            }
          : false
      );
    }
  }
}
